<template>
    <div class="h-100">
  <div class="page-header flex-row flex-wrap gap-2">
    <div class="d-none d-md-block title">Статус серверов</div>
    <div class="server-toolbar flex-row m-auto">
      <CInputGroup size="sm" class="server-toolbar-group">
        <CFormInput
          type="text"
          v-model.trim="search"
          autocomplete="off"
          placeholder="Поиск по серверам, адресам, версиям, статусам"
        />
        <CFormSelect v-model="sortBy" aria-label="Sort servers by">
          <option v-for="option in sortOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </CFormSelect>
        <CFormSelect v-model="sortDirection" aria-label="Sort direction">
          <option v-for="option in sortDirectionOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </CFormSelect>
        <CButton color="success" @click="showOnlyFresh = !showOnlyFresh">
          {{ showOnlyFresh ? 'Показать все' : 'Показать только свежие' }}
        </CButton>
      </CInputGroup>
      <div class="server-toolbar-meta">
        {{ servers.length }} / {{ totalServers }} серверов
      </div>
    </div>
  </div>
  <div class="server-status-container">
    <div class="server-grid">
        <ServerInfo
          :server="server"
          :server-state="getServerState(server)"
          v-for="server in servers"
          :key="server.guid"
          class="server-card"
        />
    </div>
  </div>
</div>
</template>

<script>
import ServerInfo from '@/components/ServerInfo.vue'
import { useDefaultStore } from '@/store';
import { checkVersion } from '@/helpers/utils';
import { CButton, CFormInput, CFormSelect, CInputGroup } from '@coreui/bootstrap-vue';
export default {
  components: {
    ServerInfo,
    CButton,
    CFormInput,
    CFormSelect,
    CInputGroup
  },
  setup() {
    const store = useDefaultStore()
    return {
      store
    }
  },
  data() {
    return {
      search: '',
      showOnlyFresh: true,
      sortBy: 'name',
      sortDirection: 'asc',
      refreshInterval: null,
      serverStates: {},
      sortOptions: [
        { value: 'name', label: 'Сортировка: название' },
        { value: 'address', label: 'Сортировка: адрес' },
        { value: 'status', label: 'Сортировка: статус' },
        { value: 'responseTime', label: 'Сортировка: отклик' },
        { value: 'version', label: 'Сортировка: версия' },
        { value: 'hostname', label: 'Сортировка: hostname' },
        { value: 'cpu', label: 'Сортировка: CPU' },
        { value: 'memory', label: 'Сортировка: RAM' },
        { value: 'recordersRunning', label: 'Сортировка: активные рекордеры' },
        { value: 'recordersStopped', label: 'Сортировка: остановленные рекордеры' },
        { value: 'jobs', label: 'Сортировка: jobs' },
        { value: 'notifications', label: 'Сортировка: уведомления' },
        { value: 'lastUpdated', label: 'Сортировка: последнее обновление' }
      ],
      sortDirectionOptions: [
        { value: 'asc', label: 'По возрастанию' },
        { value: 'desc', label: 'По убыванию' }
      ]
    }
  },
  mounted() {
    this.store.setPageTitle('Статус серверов')
    this.refreshInterval = setInterval(() => {
      this.refreshServerData()
    }, 10000)
  },
  beforeUnmount() {
    clearInterval(this.refreshInterval)
  },
  computed: {
    allServers() {
      return this.store.allDivisions || []
    },
    pollableServers() {
      return this.allServers.filter(server => checkVersion(server.version, '1.0.0.81'))
    },
    totalServers() {
      return this.allServers.length
    },
    servers() {
      if (!this.allServers.length) {
        return []
      }

      const freshFilteredServers = this.showOnlyFresh
        ? this.pollableServers
        : this.allServers

      const search = this.search.trim().toLowerCase()
      const filteredServers = search
        ? freshFilteredServers.filter(server => this.getServerSearchText(server).includes(search))
        : freshFilteredServers

      return [...filteredServers].sort((a, b) => this.compareServers(a, b))
    }
  },
  watch: {
    allServers: {
      immediate: true,
      handler(servers) {
        this.syncServerStates(servers)
      }
    }
  },
  methods: {
    createDefaultServerState(server) {
      return {
        guid: server.guid,
        data: null,
        error: null,
        loading: false,
        status: 'unknown',
        protocolChecks: {
          https: 'unknown',
          http: 'unknown'
        },
        protocolSummary: '',
        responseTime: null,
        version: server.version || null,
        hostname: null,
        os: null,
        osVersion: null,
        cpu: null,
        memory: null,
        recordersRunning: null,
        recordersStopped: null,
        jobs: null,
        notifications: null,
        lastUpdated: null
      }
    },
    normalizeServerState(server, state) {
      const data = state.data || null
      const protocolChecks = state.protocolChecks || { https: 'unknown', http: 'unknown' }

      return {
        ...state,
        guid: server.guid,
        protocolChecks,
        protocolSummary: Object.entries(protocolChecks)
          .map(([protocol, status]) => `${protocol} ${status}`)
          .join(' '),
        status: state.error ? 'error' : state.loading ? 'loading' : data?.status || 'unknown',
        responseTime: data?.response_time_ms ?? null,
        version: data?.app?.version || server.version || null,
        hostname: data?.system?.hostname || null,
        os: data?.system?.os || null,
        osVersion: data?.system?.os_version || null,
        cpu: this.getCpuNormalizedPercent(data),
        memory: this.getMemoryPercent(data),
        recordersRunning: data?.recorders?.running ?? null,
        recordersStopped: data?.recorders?.stopped ?? null,
        jobs: data?.concat_jobs?.total ?? null,
        notifications: data?.notifications?.total_stored ?? null,
        lastUpdated: data?.timestamp ? new Date(data.timestamp).getTime() : null
      }
    },
    mergeServerState(server, patch) {
      const currentState = this.serverStates[server.guid] || this.createDefaultServerState(server)
      this.serverStates = {
        ...this.serverStates,
        [server.guid]: this.normalizeServerState(server, {
          ...currentState,
          ...patch
        })
      }
    },
    syncServerStates(servers) {
      const previousStates = this.serverStates
      const nextStates = {}

      servers.forEach(server => {
        nextStates[server.guid] = previousStates[server.guid] || this.createDefaultServerState(server)
      })

      this.serverStates = nextStates

      if (!servers.length) {
        return
      }

      this.refreshProtocolChecks(servers)
      this.refreshServerData()
    },
    getServerState(server) {
      return this.serverStates[server.guid] || this.createDefaultServerState(server)
    },
    getServerSearchText(server) {
      const state = this.getServerState(server)
      return [
        server.name,
        server.address,
        server.url,
        server.version,
        server.guid,
        state.hostname,
        state.status,
        state.version,
        state.os,
        state.osVersion,
        state.protocolSummary
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    },
    getMemoryPercent(data) {
      if (!data?.memory?.total_bytes) {
        return null
      }
      return (data.memory.used_bytes / data.memory.total_bytes) * 100
    },
    getCpuNormalizedPercent(data) {
      if (!data?.cpu_usage_percent || !data?.system?.cpu_count) {
        return null
      }
      return data.cpu_usage_percent / data.system.cpu_count
    },
    async checkSingleProtocol(server, protocol) {
      try {
        const response = await fetch(`${protocol}://${server.url}/api/ping`)
        return response.ok ? 'ok' : 'error'
      } catch (e) {
        return 'error'
      }
    },
    async refreshProtocolChecks(servers = this.allServers) {
      await Promise.all(
        servers.map(async server => {
          if (!server?.url) {
            this.mergeServerState(server, {
              protocolChecks: { https: 'unknown', http: 'unknown' }
            })
            return
          }

          this.mergeServerState(server, {
            protocolChecks: { https: 'checking', http: 'checking' }
          })

          const [httpsStatus, httpStatus] = await Promise.all([
            this.checkSingleProtocol(server, 'https'),
            this.checkSingleProtocol(server, 'http')
          ])

          this.mergeServerState(server, {
            protocolChecks: { https: httpsStatus, http: httpStatus }
          })
        })
      )
    },
    async refreshServerData(servers = this.pollableServers) {
      await Promise.all(servers.map(server => this.refreshSingleServerData(server)))
    },
    async refreshSingleServerData(server) {
      const currentState = this.getServerState(server)

      this.mergeServerState(server, {
        loading: !currentState.data,
        error: null
      })

      try {
        const response = await fetch(`${window.location.protocol}//${server.url}/api/ping`)
        const resp = await response.json()

        if (resp.error) {
          this.mergeServerState(server, {
            loading: false,
            error: resp.error
          })
          return
        }

        this.mergeServerState(server, {
          data: resp.data || resp,
          loading: false,
          error: null
        })
      } catch (error) {
        this.mergeServerState(server, {
          loading: false,
          error: error.message
        })
      }
    },
    getStatusRank(status) {
      const ranks = {
        ok: 0,
        loading: 1,
        error: 2,
        unknown: 3
      }
      return ranks[status] ?? 4
    },
    getSortValue(server) {
      const state = this.getServerState(server)
      switch (this.sortBy) {
        case 'address':
          return server.address || server.url || ''
        case 'status':
          return this.getStatusRank(state.status)
        case 'responseTime':
          return state.responseTime
        case 'version':
          return state.version || server.version || ''
        case 'hostname':
          return state.hostname || ''
        case 'cpu':
          return state.cpu
        case 'memory':
          return state.memory
        case 'recordersRunning':
          return state.recordersRunning
        case 'recordersStopped':
          return state.recordersStopped
        case 'jobs':
          return state.jobs
        case 'notifications':
          return state.notifications
        case 'lastUpdated':
          return state.lastUpdated
        case 'name':
        default:
          return server.name || ''
      }
    },
    compareServers(a, b) {
      const direction = this.sortDirection === 'desc' ? -1 : 1
      const valueA = this.getSortValue(a)
      const valueB = this.getSortValue(b)
      const aMissing = valueA === null || valueA === undefined || valueA === ''
      const bMissing = valueB === null || valueB === undefined || valueB === ''

      if (aMissing && bMissing) {
        return (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true, sensitivity: 'base' })
      }
      if (aMissing) {
        return 1
      }
      if (bMissing) {
        return -1
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA !== valueB) {
          return (valueA - valueB) * direction
        }
      } else {
        const comparison = String(valueA).localeCompare(String(valueB), 'ru', {
          numeric: true,
          sensitivity: 'base'
        })
        if (comparison !== 0) {
          return comparison * direction
        }
      }

      return (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true, sensitivity: 'base' })
    }
  }
}
</script>

<style scoped>
.server-status-container {
  padding: 2rem;
  height: 100%;
}

.server-toolbar {
  min-width: min(100%, 720px);
  gap: 0.5rem;
  flex-direction: column;
}

.server-toolbar-group {
  width: 100%;
}

.server-toolbar-meta {
  font-size: 12px;
  color: #7b8794;
  text-align: right;
}

.page-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 2rem;
  color: #333;
}

.server-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  overflow-y: scroll;
  height: 90%;
  scrollbar-width: thin;
  scrollbar-color: #eee rgba(0, 255, 255, 0);
  scrollbar-arrow-color: rgba(0, 255, 255, 0);
}

@media (max-width: 767px) {
  .server-toolbar-meta {
    text-align: left;
  }
}
</style>
